import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { cn } from '../lib/utils';
import { Upload, Cpu, PackageCheck } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Upload,
    title: 'Register & Get 10 Free Credits',
    desc: 'Sign up at ocr.tyosolution.com — no credit card needed. You\'ll receive 10 free credits instantly to test the API.',
  },
  {
    number: '02',
    icon: Cpu,
    title: 'Upload Invoice + Specify Fields',
    desc: 'POST your invoice (PDF or image) along with a comma-separated list of field names. You define what to extract — vendor_name, total_amount, due_date, line_items, anything.',
  },
  {
    number: '03',
    icon: PackageCheck,
    title: 'Receive Structured JSON',
    desc: 'Get back a JSON object with each field\'s extracted value and a confidence score (0.0–1.0). Plug it directly into your ERP, database, or workflow.',
  },
];

export default function HowItWorks() {
  const [ref, visible] = useScrollAnimation();

  return (
    <section id='how-it-works' ref={ref} className={cn('py-24 bg-background reveal', visible && 'revealed')}>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        {/* Header */}
        <div className='text-center max-w-2xl mx-auto mb-16'>
          <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-primary/8 text-primary border border-primary/20 mb-4'>
            How it Works
          </span>
          <h2 className='font-heading font-bold text-4xl lg:text-5xl text-foreground mb-4'>
            From invoice to structured data in three steps
          </h2>
          <p className='text-muted-foreground text-lg'>
            Register, call the API, get JSON. That's it.
          </p>
        </div>

        {/* Steps */}
        <div className='relative'>
          {/* Connector line (desktop) */}
          <div className='hidden lg:block absolute top-16 left-[calc(16.666%+32px)] right-[calc(16.666%+32px)] h-0.5 bg-gradient-brand opacity-20' />

          <div className='grid lg:grid-cols-3 gap-8 lg:gap-6'>
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.number}
                  className={cn(
                    'relative flex flex-col items-center text-center reveal',
                    `reveal-delay-${i + 1}`,
                    visible && 'revealed'
                  )}
                >
                  {/* Icon circle */}
                  <div className='relative mb-6'>
                    <div className='w-16 h-16 rounded-2xl bg-gradient-brand flex items-center justify-center shadow-primary'>
                      <Icon className='w-7 h-7 text-primary-foreground' strokeWidth={1.8} />
                    </div>
                    <div className='absolute -top-2 -right-2 w-6 h-6 rounded-full bg-accent text-accent-foreground text-xs font-bold font-heading flex items-center justify-center'>
                      {i + 1}
                    </div>
                  </div>
                  <h3 className='font-heading font-semibold text-xl text-foreground mb-3'>{step.title}</h3>
                  <p className='text-muted-foreground text-sm leading-relaxed max-w-xs'>{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
